<script setup>
/**
 * Single‑responsibility component that hosts both the inline filter bar and the
 * mobile overlay dropdown.
 *
 * • Centralises the filter UI so parent views only import one wrapper instead
 *   of juggling two separate components (desktop bar + mobile overlay).
 * • Bridges the Pinia `filterStore.loaded` flag into template logic and shows a
 *   skeleton placeholder until backend filter metadata arrives, preventing
 *   jarring layout shifts.
 * • Manages responsive behaviour: on wide viewports the bar is always visible;
 *   on narrow screens a toggle button opens/closes a slide‑in `<teleport>`
 *   overlay, mirroring the same content for consistent UX.
 */
import {ref, computed, watch, onMounted, onBeforeUnmount} from 'vue'
import FilterBarContent from "@/components/FilterBarContent.vue";
import ResetFiltersButton from '@/components/ResetFiltersButton.vue'
import {useFilterStore} from "@/store/filter";
import {getBreakpoint} from '@/utils/breakpoint'
import {useProductListStore} from "@/store";
import SelectedFilters from "@/components/SelectedFilters.vue";

const filterStore = useFilterStore()
const productStore = useProductListStore()
const resultCount = computed(() => filterStore.itemNos.length)

/**
 * `true` when current filter combination yields zero products.
 */
const emptyFilterResult = computed(() => productStore.isEmptyFilterResult)

/**
 * Whether the mobile overlay is currently visible.
 */
const showOverlay = ref(false)

/**
 * Flag indicating the filter metadata has finished loading.
 * Used to switch between skeleton and real bar.
 */
const loaded = computed(() => filterStore.loaded)
const selectedCount = computed(() => filterStore.selectedCount)
/**
 * Array of currently selected filter objects: `{ key, value, label, desc }`.
 */
const selectedFilters = computed(() => filterStore.selectedFilters)
const currentBp = ref(0)

// which category should be refetched after reset (keeps options up to date)
const openKey = computed(() =>
  filterStore.lastInteractedKey ||
  (sessionStorage.getItem('filters:lastOpenKey') || null)
)

// Remember previous body overflow so we can restore it safely
const prevOverflowY = ref('')
const bodyLocked = ref(false)

function lockBodyScroll() {
  const body = typeof document !== 'undefined' ? document.body : null
  if (!body || bodyLocked.value) return
  prevOverflowY.value = body.style.overflowY || ''
  body.style.overflowY = 'hidden'
  bodyLocked.value = true
}

function unlockBodyScroll() {
  const body = typeof document !== 'undefined' ? document.body : null
  if (!body || !bodyLocked.value) return
  body.style.overflowY = prevOverflowY.value
  bodyLocked.value = false
}

function setHtmlFilterClass(open) {
  const html = typeof document !== 'undefined' ? document.documentElement : null
  if (!html) return
  html.classList.toggle('-open-filter-bar', open)
}

let resizeRaf = null

function handleResize() {
  if (resizeRaf) return

  resizeRaf = requestAnimationFrame(() => {
    resizeRaf = null
    const bp = getBreakpoint()
    currentBp.value = bp

    if (bp >= 2) {
      // desktop/tablet breakpoint → never lock the body
      unlockBodyScroll()
      setHtmlFilterClass(false)
    } else {
      // mobile: lock only if overlay is currently open
      if (showOverlay.value) {
        lockBodyScroll()
        setHtmlFilterClass(true)
      } else {
        unlockBodyScroll()
        setHtmlFilterClass(false)
      }
    }
  })
}

// Keep body scroll lock in sync with overlay visibility
watch(showOverlay, (open) => {
  if (open) {
    lockBodyScroll()
  } else {
    unlockBodyScroll()
  }
  setHtmlFilterClass(open)
})

const filtersReady = ref(false)

// A: mark not ready whenever selected filters truly change
watch(
  () => JSON.stringify(filterStore.selected),
  (newStr, oldStr) => {
    if (newStr === oldStr) return

    if (
      filterStore._isHydrating ||
      filterStore._isResetting ||
      filterStore.silenceSelectedMutations ||
      filterStore.loadingApply ||
      Object.values(filterStore.loadingOptions || {}).some(Boolean)
    ) {
      return
    }

    // Real user-facing change; wait for new results
    filtersReady.value = false
  }
)

// B: mark ready when filterStore finished applying filters
watch(
  loaded,
  (isLoaded) => {
    if (!isLoaded) return

    filtersReady.value = selectedCount.value > 0;
  },
  {immediate: true}
)

// Defensive: if the component mounts while already open, lock; always unlock on destroy
onMounted(() => {
  if (showOverlay.value) lockBodyScroll()

  if (selectedCount.value > 0) {
    filtersReady.value = true
  }

  window.addEventListener('resize', handleResize, {passive: true})
  // run once to sync initial state with current breakpoint
  handleResize()
})

onBeforeUnmount(() => {
  unlockBodyScroll()
  setHtmlFilterClass(false)
  window.removeEventListener('resize', handleResize)
})
</script>

<template>
  <div class="product-list--filter-bar col col-one"
       :class="{ '-full-width-filter-toggle-btn': currentBp < 2 && selectedCount === 0 }">
    <button class="filter-toggle-button btn -btn-zeta" @click="showOverlay = !showOverlay">
      <span>{{ $t('filterLabels.mobileShowFiltersBtn') || 'Choose filters' }}</span>
      <span class="filter-icon icon-menu-after"></span>
      <span v-if="currentBp < 2 && selectedCount > 0" class="filter-count-badge">{{ selectedCount }}</span>
    </button>
    <ResetFiltersButton v-if="currentBp < 2 && selectedCount > 0" :count="selectedFilters.length" :openKey="openKey"/>
    <aside class="product-list-filter-bar">
      <FilterBarContent v-if="loaded && !emptyFilterResult"/>
      <div class="product-list--filter-bar--skeleton" v-else>
        <div class="product-list--filter-bar--skeleton--item" v-if="!emptyFilterResult"></div>
        <div class="product-list--filter-bar--skeleton--item" v-if="!emptyFilterResult"></div>
        <div class="product-list--filter-bar--skeleton--item" v-if="!emptyFilterResult"></div>
        <div class="product-list--filter-bar--skeleton--item" v-if="!emptyFilterResult"></div>
        <div class="product-list--filter-bar--skeleton--item" v-if="!emptyFilterResult"></div>
        <div class="product-list--filter-bar--skeleton--item" v-if="!emptyFilterResult"></div>
      </div>
    </aside>
    <teleport to="body">
      <transition name="slide-right">
        <aside
          v-if="showOverlay" class="product-list-filter-bar filter-overlay" @click.self="showOverlay = false">
          <div class="overlay-content">
            <div class="overlay-content-inner">
              <div class="filter-bar--mobile-header">
                <span class="heading beta">{{ $t('filterLabels.mobileOverlayFilterLabel') || 'Filter' }}</span>
                <button class="overlay-close" @click="showOverlay = false">
                  <span class="icon-close-before"></span>
                </button>
              </div>
              <SelectedFilters v-if="loaded && emptyFilterResult"/>
              <FilterBarContent v-if="loaded && !emptyFilterResult"/>
              <div class="product-list--filter-bar product-list--filter-bar--skeleton col col-one" v-else>
                <div class="product-list--filter-bar--skeleton--item" v-if="!emptyFilterResult"></div>
                <div class="product-list--filter-bar--skeleton--item" v-if="!emptyFilterResult"></div>
                <div class="product-list--filter-bar--skeleton--item" v-if="!emptyFilterResult"></div>
                <div class="product-list--filter-bar--skeleton--item" v-if="!emptyFilterResult"></div>
                <div class="product-list--filter-bar--skeleton--item" v-if="!emptyFilterResult"></div>
                <div class="product-list--filter-bar--skeleton--item" v-if="!emptyFilterResult"></div>
              </div>
              <div v-if="currentBp < 2 && selectedCount > 0 && filtersReady" class="filter-bar-mobile-actions">
                <ResetFiltersButton :count="selectedFilters.length" :openKey="openKey"
                                    @click="showOverlay = !showOverlay"/>
                <button class="filter-toggle-button btn -btn-alpha" @click="showOverlay = !showOverlay">
                  <span>{{ $t('filterLabels.mobileApplyFiltersBtn', {count: resultCount}) || 'Show products' }}</span>
                </button>
              </div>
            </div>
          </div>
        </aside>
      </transition>
    </teleport>
  </div>
</template>
